/* @version 5 */
/*
 * uq-buybox.js — controller for the unified PDP buy-box.
 *
 * Reads JSON config from <script type="application/json" data-uq-buybox-config>
 * embedded inside the <uq-buybox> element. Manages purchase-mode toggle,
 * variant-axis selection (Flavor / Packets / Supply), price + savings recompute,
 * VWO auto-discount display, and ATC dispatch via cart/add.js.
 *
 * Config shape:
 * {
 *   mode: 'subscribe' | 'onetime',           // default
 *   regretNudge: string | null,
 *   variants: [
 *     { id, available, price, compareAtPrice, options: { Flavor, Packets, Supply, ... } }
 *   ],
 *   axes: [{ name, position, values: [{label, handle}] }],   // product.options
 *   sellingPlans: { '1mo': <id>, '3mo': <id> } | null,       // optional supply→plan map
 *   supplyAxisName: 'Supply' | null,                          // which axis maps to 1mo/3mo
 *   defaultSupply: '1 Month',                                 // default value of supply axis
 *   showSubBenefits: bool,
 *   bundle: { name, handle, version, items: [...] } | undefined,     // ATC unbundling only —
 *     // driven by the bundle_metaobject_handle block setting; see uq-buybox.js isBundle usage.
 *   anchorPackets: [{ count, otPrice, subPrice }] | undefined,
 *     // (cents) the "anchor_product" block setting's own per-packet-count price after the
 *     // combo discount (this page's own product IS the combo SKU, e.g. "Flush & Defend";
 *     // the anchor, e.g. Flush, is a different product set directly on the block — no
 *     // bundle/metaobject dependency). Computed server-side per packet count and per mode —
 *     // see main-product-unified-subscription.liquid. When a pill's count matches an entry,
 *     // Packets pill-meta uses it (picking otPrice/subPrice by mode) instead of that pill's
 *     // own variant price.
 * }
 */

(function () {
  if (customElements.get('uq-buybox')) return;

  function $(scope, sel) { return scope.querySelector(sel); }
  function $$(scope, sel) { return Array.from(scope.querySelectorAll(sel)); }

  function moneyToFloat(cents) {
    if (typeof cents === 'number') return cents / 100;
    return parseFloat(cents) || 0;
  }

  function fmtMoney(amount) {
    if (amount == null || isNaN(amount)) return '';
    return `$${amount.toFixed(2)}`;
  }

  function cssAttrValue(value) {
    const stringValue = String(value);
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(stringValue);
    }
    return stringValue.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  class UQBuyBox extends HTMLElement {
    constructor() {
      super();
      try {
        const cfgEl = this.querySelector('script[data-uq-buybox-config]');
        this.config = cfgEl ? JSON.parse(cfgEl.textContent) : {};
      } catch (e) {
        console.error('[uq-buybox] invalid config JSON', e);
        this.config = {};
      }
      this.cart = document.querySelector('cart-drawer');
      this.state = {
        mode: this.config.mode || 'subscribe',
        selections: {}, // { axisName: handle }
      };
      this.autoDiscountConfig = { enabled: false, code: '', percentage: 0 };
      this._boundAutoDiscountUpdated = this._onAutoDiscountUpdated.bind(this);
      this.selectedGwpVariantId = null;
      this.selectedGwpCampaign = null;
      this._boundGwpSelected = (event) => {
        this.selectedGwpVariantId = event.detail.variantId;
        this.selectedGwpCampaign = event.detail.campaign || null;
      };
      this._boundGwpDeselected = () => {
        this.selectedGwpVariantId = null;
        this.selectedGwpCampaign = null;
      };
      this._initialized = false;
      this._armObserver = null;
    }

    connectedCallback() {
      if (this._initialized) return;
      if (this.hasAttribute('data-uq-ab-managed') && !this._isVariationActive()) {
        this._observeVariationActivation();
        return;
      }
      this._initialize();
    }

    _isVariationActive() {
      const qaArm = document.body?.getAttribute('data-uq-buybox-qa');
      if (qaArm === 'legacy') return false;
      return qaArm === 'new' || document.body?.classList.contains('vwo-new-buybox');
    }

    _observeVariationActivation() {
      if (this._armObserver || !document.body) return;
      this._armObserver = new MutationObserver(() => {
        if (!this._isVariationActive()) return;
        this._armObserver.disconnect();
        this._armObserver = null;
        this._initialize();
      });
      try {
        this._armObserver.observe(document.body, {
          attributes: true,
          attributeFilter: ['class', 'data-uq-buybox-qa'],
        });
      } catch (error) {
        this._armObserver = null;
        console.warn('[uq-buybox] arm observer unavailable', error);
      }
    }

    _initialize() {
      if (this._initialized || !this.isConnected) return;
      this._initialized = true;
      window.addEventListener(this._autoDiscountEventName(), this._boundAutoDiscountUpdated);
      document.addEventListener('gwp:selected', this._boundGwpSelected);
      document.addEventListener('gwp:deselected', this._boundGwpDeselected);
      this._setAutoDiscountConfig(
        window.UqoraAutoDiscount?.getLastEmittedConfig?.() ||
          window.UqoraAutoDiscount?.getConfig?.()
      );
      this._applyAutoDiscountCode();
      this._initialiseSelections();
      this._bindToggle();
      this._bindSelectors();
      this._bindPills();
      this._bindCta();
      this._bindLearnMore();
      this._bindFlushQuantityHelp();
      this._render();
    }

    disconnectedCallback() {
      this._armObserver?.disconnect();
      this._armObserver = null;
      window.removeEventListener(this._autoDiscountEventName(), this._boundAutoDiscountUpdated);
      document.removeEventListener('gwp:selected', this._boundGwpSelected);
      document.removeEventListener('gwp:deselected', this._boundGwpDeselected);
    }

    _autoDiscountEventName() {
      return window.UqoraAutoDiscount?.events?.stateUpdated || 'uqora:auto-discount-updated';
    }

    _setAutoDiscountConfig(nextConfig) {
      const next = nextConfig || {};
      this.autoDiscountConfig = {
        enabled: Boolean(next.enabled),
        code: String(next.code || ''),
        percentage: Number(next.percentage) || 0,
      };
    }

    _onAutoDiscountUpdated(event) {
      if (this.hasAttribute('data-uq-ab-managed') && !this._isVariationActive()) return;
      this._setAutoDiscountConfig(
        event.detail || window.UqoraAutoDiscount?.getConfig?.()
      );
      this._applyAutoDiscountCode();
      this._render();
    }

    _applyAutoDiscountCode() {
      if (!this.autoDiscountConfig.enabled || !window.UqoraAutoDiscount) return;
      window.UqoraAutoDiscount.applyDiscountCode(this.autoDiscountConfig.code);
    }

    _applyAutoDiscountToPrice(price) {
      const numericPrice = Number(price);
      if (!Number.isFinite(numericPrice)) return 0;
      if (!this.autoDiscountConfig.enabled) return numericPrice;
      if (window.UqoraAutoDiscount?.applyDiscountToPrice) {
        return window.UqoraAutoDiscount.applyDiscountToPrice(
          numericPrice,
          this.autoDiscountConfig
        );
      }
      const priceInCents = Math.round(numericPrice * 100);
      const discountInCents = Math.floor(
        (priceInCents * this.autoDiscountConfig.percentage) / 100
      );
      return (priceInCents - discountInCents) / 100;
    }

    // Exact-percentage variant for the anchor (combo) packet totals. Those are
    // synthetic fractional figures (see the anchorPackets calc in
    // main-product-unified-subscription.liquid), not a real cart price, so the
    // cents-floor mirror above would round the total first and can tip the
    // displayed cent (Flush Advanced+ Regimen 10 packets: $1.50, not $1.51).
    _applyAutoDiscountExact(price) {
      const numericPrice = Number(price);
      if (!Number.isFinite(numericPrice)) return 0;
      const { enabled, percentage } = this.autoDiscountConfig;
      if (!enabled || !(percentage > 0)) return numericPrice;
      return numericPrice * (1 - percentage / 100);
    }

    // Renders the "✅ CODE discount auto-applied" callout plus the renewal note
    // beneath the price. Returns true when the renewal note took over the cadence
    // copy, so the caller can drop it from the headline (see _renderPrice).
    _renderAutoDiscountMeta(renewalPrice, cadenceCopy) {
      const note = $(this, '[data-uq-price-renewal]');
      const callout = $(this, '[data-uq-auto-discount]');
      const code = $(this, '[data-uq-auto-discount-code]');
      const enabled = this.autoDiscountConfig.enabled;
      const firstPaymentCadence = /(?:3\s*months|12\s*weeks|quarterly)/i.test(
        cadenceCopy || ''
      )
        ? ' quarterly'
        : (cadenceCopy || '');

      if (callout) callout.hidden = !enabled;
      if (code) code.textContent = this.autoDiscountConfig.code || 'TRYUQORA';

      const showRenewal =
        enabled && this.state.mode === 'subscribe' && Number(renewalPrice) > 0;
      if (note) {
        note.hidden = !showRenewal;
        note.textContent = showRenewal
          ? `${fmtMoney(renewalPrice)}${firstPaymentCadence} after first payment`
          : '';
      }
      return showRenewal;
    }

    _bindLearnMore() {
      const link = $(this, '[data-uq-learn-more]');
      if (!link) return;
      link.addEventListener('click', (e) => {
        e.preventDefault();
        // Reuse the existing modal shipped via snippets/subscription-benefits-dialog.liquid
        // (legacy single_subscription pattern); fall back to the production
        // #subscribeDetailsModal dialog if it's mounted globally.
        const modal =
          document.querySelector('#subscription-benefits') ||
          document.querySelector('#subscribeDetailsModal');
        if (modal && typeof modal.showModal === 'function') {
          modal.showModal();
        } else if (modal) {
          modal.setAttribute('open', 'true');
        }
      });
    }

    _bindFlushQuantityHelp() {
      const trigger = $(this, '[data-uq-flush-quantity-help]');
      const dialog = document.querySelector('#how-many-flush-dialog');
      if (!trigger || !dialog) return;

      trigger.addEventListener('click', () => {
        dialog.show();
      });
    }

    /* ─────── State ─────── */

    _initialiseSelections() {
      const variants = this.config.variants || [];
      const configuredVariant = variants.find(
        (v) => String(v.id) === String(this.config.selectedVariantId)
      );
      let initialVariant = configuredVariant;
      let requestedSupply = null;
      let requestedId = null;
      let requestedSubscription = null;

      try {
        // A delayed VWO assignment must hydrate the shopper's landing selection,
        // not a default the legacy controller wrote while it was still visible.
        const entryUrl = this.hasAttribute('data-uq-ab-managed') && window.UqoraBuyboxEntryUrl;
        const params = new URL(entryUrl || window.location.href).searchParams;
        requestedId = params.get('variant');
        requestedSubscription = params.get('subscription');
      } catch (_) {
        // Keep server-selected defaults when the URL cannot be parsed.
      }

      const requestedPdpVariant = variants.find(
        (v) => String(v.id) === String(requestedId)
      );
      if (requestedPdpVariant) initialVariant = requestedPdpVariant;

      if (requestedSubscription === 'one-time-purchase') {
        this.state.mode = 'onetime';
      } else if (requestedSubscription === 'default-selling-plan') {
        this.state.mode = this._canSubscribe() ? 'subscribe' : 'onetime';
      }

      if (this._isMP()) {
        const mpVariants = this.config.multiProduct?.threeMonthVariants || [];
        const requestedMpVariant = mpVariants.find(
          (v) => String(v.id) === String(requestedId)
        );

        // The variant is the strongest signal because it identifies the product
        // Shopify will actually add. A 3-month product variant therefore wins over
        // a stale/conflicting subscription param left by an older URL writer.
        if (requestedMpVariant) {
          const pairedPdpVariant = variants.find(
            (v) => this._pickThreeMonthVariant([requestedMpVariant], v)
          );
          if (
            pairedPdpVariant &&
            this._canSubscribe() &&
            this.config.multiProduct?.threeMonthSellingPlanId
          ) {
            initialVariant = pairedPdpVariant;
            requestedSupply = '3 Months';
            this.state.mode = 'subscribe';
          }
        } else if (requestedPdpVariant) {
          requestedSupply = this.config.defaultSupply;
        } else if (requestedSubscription === 'multi-product') {
          const fallbackVariant =
            initialVariant || variants.find((v) => v.available) || variants[0];
          if (
            this._canSubscribe() &&
            this.config.multiProduct?.threeMonthSellingPlanId &&
            this._pickThreeMonthVariant(mpVariants, fallbackVariant)
          ) {
            requestedSupply = '3 Months';
            this.state.mode = 'subscribe';
          }
        } else if (
          requestedSubscription === 'default-selling-plan' ||
          requestedSubscription === 'one-time-purchase'
        ) {
          requestedSupply = this.config.defaultSupply;
        }
      }

      initialVariant = initialVariant || variants.find((v) => v.available) || variants[0];
      if (!initialVariant) return;
      const axes = this.config.axes || [];
      axes.forEach((axis) => {
        const value = initialVariant.options[axis.name];
        if (value != null) this.state.selections[axis.name] = value;
      });

      // Preserve URL-derived cadence on cold load/reload. Only use the configured
      // default when the URL did not identify a valid cadence.
      if (this.config.supplyAxisName && this.config.defaultSupply) {
        this.state.selections[this.config.supplyAxisName] =
          requestedSupply ||
          this.state.selections[this.config.supplyAxisName] ||
          this.config.defaultSupply;
      }
    }

    _findVariant(mode = this.state.mode) {
      return this._matchVariant(this._effectiveSelections(mode));
    }

    // Match a variant against a selection map, ignoring any selection key that is
    // not a real product axis. The multi-product mode's synthetic "Delivery
    // Frequency" selection lives in state but no variant carries that option, so it
    // must be excluded from matching (otherwise every variant fails the test).
    _matchVariant(sel) {
      const variants = this.config.variants || [];
      const axisNames = new Set((this.config.axes || []).map((a) => a.name));
      return variants.find((v) =>
        Object.entries(sel).every(
          ([axis, val]) => !axisNames.has(axis) || v.options[axis] === val
        )
      );
    }

    // The supply/frequency axis is a subscribe-only concept (its picker is hidden in
    // one-time mode). A frequency the customer chose while subscribing must not leak
    // into the one-time price or the resolved one-time variant, so in one-time mode we
    // reset that axis to the default supply. This keeps the one-time price free of any
    // frequency-derived discount without hardcoding it — the resolved default-supply
    // variant still runs through the normal price logic (genuine compare_at, etc.).
    _effectiveSelections(mode = this.state.mode) {
      const sel = { ...this.state.selections };
      const supplyName = this.config.supplyAxisName;
      if (mode !== 'subscribe' && supplyName && this.config.defaultSupply) {
        sel[supplyName] = this.config.defaultSupply;
      }
      return sel;
    }

    /* ─────── Bindings ─────── */

    _bindToggle() {
      $$(this, '[data-uq-toggle]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const target = btn.getAttribute('data-uq-toggle');
          if (!target || target === this.state.mode) return;
          this.state.mode = target;
          this._render();
        });
      });
    }

    // Desktop frequency selectors (Monthly / Every 3 months / One-time) set the
    // same mode + supply state the tabs and frequency pills do, so pricing, URL
    // sync and ATC need no layout awareness. Visibility is CSS-only.
    _bindSelectors() {
      $$(this, '[data-uq-selector]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const mode = btn.getAttribute('data-uq-selector');
          if (mode !== 'subscribe' && mode !== 'onetime') return;
          const supply = btn.getAttribute('data-uq-selector-supply');
          const supplyName = this.config.supplyAxisName;
          this.state.mode = mode;
          if (supply && supplyName) this.state.selections[supplyName] = supply;
          this._render();
        });
      });
    }

    _bindPills() {
      $$(this, '[data-uq-pill]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const axis = btn.getAttribute('data-uq-axis');
          const value = btn.getAttribute('data-uq-value');
          if (!axis || value == null) return;
          this.state.selections[axis] = value;
          this._render();
        });
      });
    }

    _bindCta() {
      const cta = $(this, '[data-uq-cta]');
      if (!cta) return;
      cta.addEventListener('click', () => this._addToCart());
    }

    /* ─────── Render ─────── */

    _render() {
      if (this.hasAttribute('data-uq-ab-managed') && !this._isVariationActive()) return;
      this._normaliseUnavailableMpCadence();
      this.setAttribute('data-buybox-mode', this.state.mode);

      // Toggle highlights
      $$(this, '[data-uq-toggle]').forEach((btn) => {
        const target = btn.getAttribute('data-uq-toggle');
        btn.classList.toggle('is-active', target === this.state.mode);
        btn.setAttribute('aria-selected', String(target === this.state.mode));
      });

      // Pill highlights
      $$(this, '[data-uq-pill]').forEach((btn) => {
        const axis = btn.getAttribute('data-uq-axis');
        const value = btn.getAttribute('data-uq-value');
        const active = this.state.selections[axis] === value;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-checked', String(active));
      });

      // Pricing
      const variant = this._findVariant();
      this._renderPrice(variant);
      this._renderSavings(variant);
      this._renderCta(variant);
      this._updatePerPacketPrices();
      this._renderTogglePrices();
      this._renderSelectors();
      this._updateRenewsCopy();
      this._syncGallery(variant);
      this._syncUrlVariant(variant);
    }

    _syncGallery(variant) {
      if (!variant) return;
      const gallery = document.querySelector('.mediaGallery-container');
      const availableHandles = new Set(
        String(gallery?.getAttribute('data-alt-text-list') || '')
          .trim()
          .split(/\s+/)
          .filter(Boolean)
      );
      if (!gallery || !availableHandles.size) return;

      for (const axis of this.config.axes || []) {
        const selectedValue = variant.options?.[axis.name];
        const configuredValue = (axis.values || []).find(
          (value) => value.handle === selectedValue
        );
        const mediaHandle = configuredValue?.mediaHandle;
        if (!mediaHandle || !availableHandles.has(mediaHandle)) continue;
        gallery.setAttribute('data-active-variant-handle', mediaHandle);
        return;
      }
    }

    _updateRenewsCopy() {
      const el = $(this, '[data-uq-renews]');
      if (!el) return;
      const supplyName = this.config.supplyAxisName;
      const supplyVal = supplyName ? String(this.state.selections[supplyName] || '') : '';
      const is3mo = /3\s*mo|12\s*we/i.test(supplyVal);
      if (is3mo) { el.textContent = 'Renews every 12 weeks'; return; }
      el.textContent = this.config.renewsCopy || 'Renews every 4 weeks';
    }

    _syncUrlVariant(variant) {
      // Every unified PDP owns both its resolved variant and the matching
      // legacy-compatible subscription type. Multi-product mode additionally swaps
      // the resolved PDP variant for the paired 3-month product variant, so copied
      // links cannot carry a contradictory variant/cadence pair.
      let subscriptionType =
        this.state.mode === 'subscribe' ? 'default-selling-plan' : 'one-time-purchase';
      if (this._isMP()) {
        const t = this._mpTarget();
        if (t) {
          variant = { id: t.variantId };
          subscriptionType = t.kind;
        }
      }
      if (!variant || !window.history || !window.history.replaceState) return;
      try {
        const url = new URL(window.location.href);
        const currentVariant = url.searchParams.get('variant');
        const currentSubscription = url.searchParams.get('subscription');
        const variantMatches = String(currentVariant || '') === String(variant.id);
        const subscriptionMatches =
          !subscriptionType || currentSubscription === subscriptionType;
        if (variantMatches && subscriptionMatches) return;
        url.searchParams.set('variant', String(variant.id));
        if (subscriptionType) url.searchParams.set('subscription', subscriptionType);
        window.history.replaceState(window.history.state, '', url.toString());
      } catch (e) {
        /* no-op */
      }
    }

    _activeSellingPlanId() {
      const plans = this.config.sellingPlans || {};
      const supplyName = this.config.supplyAxisName;
      if (supplyName && this.state.selections[supplyName]) {
        const supplyVal = String(this.state.selections[supplyName]);
        if (/3\s*mo|12\s*we/i.test(supplyVal)) return plans['3mo'] || null;
        return plans['1mo'] || null;
      }
      return plans['1mo'] || null;
    }

    _activeAdjustment() {
      const planId = this._activeSellingPlanId();
      if (!planId) return null;
      const adjustments = this.config.sellingPlanAdjustments || {};
      return adjustments[String(planId)] || null;
    }

    _supplyMonths(supplyValue) {
      // "1 Month" → 1, "3 Months" → 3, etc. Defaults to 1 if no parse.
      if (!supplyValue) return 1;
      const value = String(supplyValue);
      const months = value.match(/(\d+)\s*mo/i);
      if (months) return parseInt(months[1], 10) || 1;
      const weeks = value.match(/(\d+)\s*we/i);
      if (weeks) return Math.max(1, Math.round((parseInt(weeks[1], 10) || 4) / 4));
      return 1;
    }

    _computePrices(variant, mode = this.state.mode) {
      // Returns dollars: { otPrice, compareAt, subPrice, perMonthPrice, perMonthBase, months }
      // perMonthPrice/perMonthBase are the values used for /month display + savings math.
      //
      // Both the 1-month and 3-month variants are configured identically in Shopify
      // Admin: each carries its full (undiscounted) price + optional compare_at on the
      // variant, and the per-cadence subscription discount comes from the selling
      // plan's price_adjustment — NOT from a pre-discounted variant price. The
      // adjustment is therefore applied for every cadence; _activeSellingPlanId
      // resolves the correct (1mo/3mo) plan from the selected supply value, so the
      // 3-month variant picks up the 90-day plan's adjustment exactly the way the
      // 1-month variant picks up the 30-day plan's.
      const otPrice = moneyToFloat(variant.price);
      const compareAtRaw = moneyToFloat(variant.compareAtPrice);
      const compareAt = compareAtRaw > otPrice ? compareAtRaw : null;

      const supplyName = this.config.supplyAxisName;
      const supplyValue = supplyName ? this._effectiveSelections(mode)[supplyName] : null;
      const months = this._supplyMonths(supplyValue);

      let subPrice = otPrice;
      const adj = this._activeAdjustment();
      if (adj) {
        if (adj.type === 'percentage') {
          subPrice = otPrice - (otPrice * (adj.value || 0)) / 100;
        } else if (adj.type === 'fixed_amount') {
          subPrice = otPrice - moneyToFloat(adj.value || 0);
        } else if (adj.type === 'price') {
          subPrice = moneyToFloat(adj.value || 0);
        }
      }
      if (subPrice < 0) subPrice = 0;

      // Per-month figures used for display + savings comparison. The strike-through
      // baseline is what the customer would pay buying month-to-month: the 1-month
      // sibling variant's compare_at (or price). A multi-month variant's own price
      // already embeds the bulk discount, so it can't serve as its own baseline —
      // e.g. a $77.97 3-month variant would strike through $25.99 instead of the
      // 1-month $34.99. Falls back to the selected variant's own figures when no
      // 1-month sibling resolves (single-variant products, unusual axes).
      const perMonthPrice = subPrice / Math.max(months, 1);
      let perMonthBase = (compareAt || otPrice) / Math.max(months, 1);
      if (supplyName && months > 1) {
        const axis = this._findAxisByName(supplyName);
        const oneMonthValue =
          axis && (axis.values || []).find((v) => this._supplyMonths(v.handle) === 1);
        if (oneMonthValue) {
          const sibling = this._matchVariant({
            ...this._effectiveSelections(mode),
            [supplyName]: oneMonthValue.handle,
          });
          if (sibling) {
            const sPrice = moneyToFloat(sibling.price);
            const sCompare = moneyToFloat(sibling.compareAtPrice);
            if (sPrice > 0) perMonthBase = sCompare > sPrice ? sCompare : sPrice;
          }
        }
      }

      return { otPrice, compareAt, subPrice, perMonthPrice, perMonthBase, months };
    }

    _renderPrice(variant) {
      if (this._isMP()) return this._mpRenderPrice();
      const compareEl = $(this, '[data-uq-price-compare]');
      const currentEl = $(this, '[data-uq-price-current]');
      const suffixEl = $(this, '[data-uq-price-suffix]');
      if (!variant || !currentEl) return;

      const { otPrice, compareAt, subPrice, perMonthPrice, perMonthBase, months } =
        this._computePrices(variant);

      let current;
      let displayCompareAt = null;
      let suffix = '';
      if (this.state.mode === 'subscribe') {
        if (months > 1) {
          current = subPrice;
          displayCompareAt = perMonthBase * months;
          suffix = ' every 3 months';
        } else {
          current = perMonthPrice;
          displayCompareAt = perMonthBase;
          suffix = ' monthly';
        }
      } else {
        current = otPrice;
        displayCompareAt = compareAt;
      }

      const renewalPrice = current;
      current = this._applyAutoDiscountToPrice(current);
      if (
        this.autoDiscountConfig.enabled &&
        (!displayCompareAt || displayCompareAt <= current)
      ) {
        displayCompareAt = renewalPrice;
      }
      currentEl.textContent = fmtMoney(current);
      if (compareEl) {
        if (displayCompareAt && displayCompareAt > current) {
          compareEl.textContent = fmtMoney(displayCompareAt);
          compareEl.style.display = '';
        } else {
          compareEl.textContent = '';
          compareEl.style.display = 'none';
        }
      }
      // When the auto-discount is applied the headline shows a first-order-only
      // price, so the cadence word ("monthly" / "every 3 months") moves to the
      // renewal note beneath it — next to the discounted figure it would misread
      // as the recurring charge (client QA, 2026-08-05).
      const cadenceMovedToRenewal = this._renderAutoDiscountMeta(renewalPrice, suffix);
      if (suffixEl) suffixEl.textContent = cadenceMovedToRenewal ? '' : suffix;
    }

    // Price inside each Subscribe & Save / One-time Purchase tab. Desktop only —
    // visibility is CSS-gated (.uq-buybox__toggle-price in uq-buybox.css); the
    // figures are always written. Each tab shows the same discounted headline the
    // price row would show in that mode; a multi-month subscription shows its
    // per-month share ("$44.80 each") per Ben's design (Asana "New Buybox
    // Updates", 2026-09-04).
    _renderTogglePrices() {
      $$(this, '[data-uq-toggle-price]').forEach((el) => {
        el.textContent = this._togglePriceFor(el.getAttribute('data-uq-toggle-price'));
      });
    }

    _togglePriceFor(mode) {
      if (mode !== 'subscribe' && mode !== 'onetime') return '';
      if (mode === 'subscribe' && !this._canSubscribe()) return '';
      const variant = this._findVariant(mode);
      if (!variant) return '';
      let current;
      let months = 1;
      if (this._isMP()) {
        const figures = this._mpComputePrices(variant, mode);
        current = figures.current;
        months = figures.months || 1;
      } else {
        const figures = this._computePrices(variant, mode);
        if (mode === 'subscribe') {
          months = figures.months || 1;
          current = months > 1 ? figures.subPrice : figures.perMonthPrice;
        } else {
          current = figures.otPrice;
        }
      }
      current = this._applyAutoDiscountToPrice(current);
      if (!(current > 0)) return '';
      if (mode === 'subscribe' && months > 1) return `${fmtMoney(current / months)} each`;
      return fmtMoney(current);
    }

    _selectorIsActive(btn) {
      const mode = btn.getAttribute('data-uq-selector');
      if (mode !== this.state.mode) return false;
      if (mode !== 'subscribe') return true;
      const supply = btn.getAttribute('data-uq-selector-supply');
      const supplyName = this.config.supplyAxisName;
      return !supply || !supplyName || this.state.selections[supplyName] === supply;
    }

    // Each subscribe selector shows "Save X%": the same figure the yellow
    // savings callout renders once that option is chosen (auto-discount
    // included), so the button and the callout never disagree.
    _renderSelectors() {
      $$(this, '[data-uq-selector]').forEach((btn) => {
        const active = this._selectorIsActive(btn);
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-checked', String(active));
        const meta = $(btn, '[data-uq-selector-meta]');
        if (!meta) return;
        const pct = this._selectorSavingsPct(
          btn.getAttribute('data-uq-selector'),
          btn.getAttribute('data-uq-selector-supply')
        );
        meta.textContent = pct > 0 ? `Save ${pct}%` : '';
      });
    }

    // The price helpers read the live mode + cadence, so the option is applied
    // to state for the calculation and restored before returning.
    _selectorSavingsPct(mode, supplyValue) {
      if (mode !== 'subscribe' || !this._canSubscribe()) return 0;
      const selections = this.state.selections;
      const supplyName = this.config.supplyAxisName;
      const prevMode = this.state.mode;
      const hadSupply = Boolean(supplyName) && Object.prototype.hasOwnProperty.call(selections, supplyName);
      const prevSupply = hadSupply ? selections[supplyName] : undefined;
      this.state.mode = mode;
      if (supplyName && supplyValue) selections[supplyName] = supplyValue;
      try {
        if (this._isMP()) return this._mpTarget() ? this._mpSavingsPct() : 0;
        const variant = this._findVariant();
        return variant ? this._savingsPct(variant) : 0;
      } finally {
        this.state.mode = prevMode;
        if (hadSupply) selections[supplyName] = prevSupply;
        else if (supplyName) delete selections[supplyName];
      }
    }

    // Savings % behind the yellow callout, computed from the exact headline
    // figures. Multi-month options use their full renewal total and the
    // equivalent multi-month retail baseline.
    _savingsPct(variant) {
      const { otPrice, compareAt, subPrice, perMonthPrice, perMonthBase, months } =
        this._computePrices(variant);
      let pct = 0;
      if (this.state.mode === 'subscribe') {
        const renewalPrice = months > 1 ? subPrice : perMonthPrice;
        const displayPrice = this._applyAutoDiscountToPrice(renewalPrice);
        const baseline = months > 1 ? perMonthBase * months : perMonthBase;
        if (baseline > 0 && displayPrice < baseline) {
          pct = Math.round(((baseline - displayPrice) / baseline) * 100);
        }
      } else {
        const displayPrice = this._applyAutoDiscountToPrice(otPrice);
        const baseline = compareAt || (this.autoDiscountConfig.enabled ? otPrice : 0);
        if (baseline > 0 && displayPrice < baseline) {
          pct = Math.round(((baseline - displayPrice) / baseline) * 100);
        }
      }
      return pct;
    }

    _renderSavings(variant) {
      if (this._isMP()) return this._mpRenderSavings();
      const savingsEl = $(this, '[data-uq-savings]');
      if (!savingsEl || !variant) return;
      const cfg = this.config;
      const pct = this._savingsPct(variant);

      // Pick template copy strictly from the block setting — no default fallback,
      // so an empty setting never renders the yellow callout (either mode).
      let template = '';
      if (this.state.mode === 'subscribe') {
        template = cfg.bundleSavingsSub || '';
      } else {
        template = cfg.bundleSavingsOnetime || '';
      }

      // Substitute {pct} with computed percentage. Also handle hardcoded "10%"-style
      // values in legacy templates by leaving them alone if no placeholder is present.
      const copy = template.replace(/\{pct\}/g, String(pct));

      // Hide at 0% savings. In subscribe mode this applies even when the
      // bundle_savings_sub block setting supplies the copy — a "You're saving 0%!"
      // callout on an undiscounted option (e.g. 1-month plan with no discount)
      // must not render; it reappears when a discounted option (3-month) is picked.
      // One-time mode keeps the legacy rule: a static onetime override renders as-is.
      // `!copy.trim()` treats an empty OR whitespace-only setting as "no copy".
      const shouldHide =
        !copy.trim() || (pct === 0 && (this.state.mode === 'subscribe' || !cfg.bundleSavingsOnetime));

      savingsEl.innerHTML = shouldHide ? '' : this._savingsMarkup(copy);
      this.setAttribute('data-savings-visible', shouldHide ? 'false' : 'true');
    }

    // The yellow highlight sits on an inline mark span rather than the <p> itself,
    // so it wraps line-by-line (see .uq-buybox__savings-mark in uq-buybox.css).
    // `copy` is merchant-authored theme-block text (bundle_savings_sub /
    // bundle_savings_onetime) and was already written via innerHTML to support
    // inline markup in the setting — wrapping it changes no trust boundary.
    _savingsMarkup(copy) {
      return `<span class="uq-buybox__savings-mark">${copy}</span>`;
    }

    _renderCta(variant) {
      const cta = $(this, '[data-uq-cta]');
      if (!cta) return;
      if (this._isMP()) {
        const t = this._mpTarget();
        const enabledMP = !!(t && t.available);
        cta.disabled = !enabledMP;
        cta.textContent = enabledMP ? 'Add to cart' : 'Unavailable';
        return;
      }
      const hasRequiredPlan =
        this.state.mode !== 'subscribe' || Boolean(this._resolveSellingPlanId(variant));
      const enabled = !!(variant && variant.available && hasRequiredPlan);
      cta.disabled = !enabled;
      cta.textContent = enabled
        ? 'Add to cart'
        : this.state.mode === 'subscribe' && variant?.available
          ? 'Subscription unavailable'
          : 'Unavailable';
    }

    _updatePerPacketPrices() {
      if (this._isMP()) return this._mpUpdatePerPacketPrices();
      // For Packets axis pills, update the per-packet price display based on the
      // currently-selected non-packets axes + current mode + selling plan adjustment.
      const packetsAxis = this._findAxisByName('Packets') || this._findAxisByName('packets');
      if (!packetsAxis) return;
      const variants = this.config.variants || [];
      const sel = this._effectiveSelections();
      // Combo mode (anchor_product block setting): the anchor's own per-packet-count
      // price, already discounted by the combo savings % for this specific packet
      // count and mode — computed server-side (each packet size is a distinct SKU on
      // both the combo and the anchor, so this isn't one price divided three ways).
      // See main-product-unified-subscription.liquid for the anchorPackets calc.
      const anchorPackets = this.config.anchorPackets || [];

      packetsAxis.values.forEach((v) => {
        const count = parseInt(String(v.handle).replace(/[^0-9]/g, ''), 10) || 0;
        if (!count) return;
        const bundleEntry = anchorPackets.find((p) => p.count === count);
        let usePrice;
        let billingMonths = 1;
        if (bundleEntry) {
          usePrice = moneyToFloat(this.state.mode === 'subscribe' ? bundleEntry.subPrice : bundleEntry.otPrice);
        } else {
          const trial = { ...sel, [packetsAxis.name]: v.handle };
          const variant = variants.find((x) =>
            Object.entries(trial).every(([axis, val]) => x.options[axis] === val)
          );
          if (!variant) return;
          const { otPrice, subPrice, months } = this._computePrices(variant);
          usePrice = this.state.mode === 'subscribe' ? subPrice : otPrice;
          if (this.state.mode === 'subscribe') billingMonths = months;
        }
        // The pill mirrors the headline: with a code auto-applied it shows the
        // first-order (discounted) basis, not the renewal basis (Asana "New Buybox
        // Updates", 2026-09-04). Real variant totals use the headline's cents-floor
        // math; anchor totals take the exact percentage (_applyAutoDiscountExact).
        const perPacket = this._perPacketPrice(
          bundleEntry
            ? this._applyAutoDiscountExact(usePrice)
            : this._applyAutoDiscountToPrice(usePrice),
          count,
          billingMonths
        );
        const safeAxisName = cssAttrValue(packetsAxis.name);
        const safeValue = cssAttrValue(v.handle);
        const metaEl = this.querySelector(
          `[data-uq-pill][data-uq-axis="${safeAxisName}"][data-uq-value="${safeValue}"] [data-uq-pill-meta]`
        );
        if (metaEl) metaEl.textContent = `$${perPacket.toFixed(2)} / packet`;
      });
    }

    _perPacketPrice(totalPrice, packetCount, billingMonths = 1) {
      const count = Math.max(Number(packetCount) || 0, 0);
      if (!count) return 0;
      const months = Math.max(Number(billingMonths) || 1, 1);
      return Number(totalPrice || 0) / (count * months);
    }

    _findAxisByName(name) {
      return (this.config.axes || []).find(
        (a) => (a.name || '').toLowerCase() === String(name).toLowerCase()
      );
    }

    /* ─────── Multi-product (metaobject) mode ───────
     *
     * When config.multiProduct is present this block reproduces the legacy
     * main-product-multi-product-subscriptions behavior inside the unified UI:
     *
     *   One-time              → PDP variant, no plan         (one-time-purchase)
     *   Subscribe / 1 Month   → PDP variant + PDP plan       (default-selling-plan)
     *   Subscribe / 3 Months  → 3-month product's variant    (multi-product)
     *
     * The 3-month product is a SEPARATE product; its variant is paired to the PDP
     * variant by identical handle, else by the "<sku>-90" SKU convention.
     */

    _isMP() {
      return !!(this.config && this.config.multiProduct);
    }

    _canSubscribe() {
      if (this.config?.hasSellingPlans === false) return false;
      if (this._isMP()) return Boolean(this.config.multiProduct?.pdpSellingPlanId);
      return Boolean(this.config?.sellingPlans?.['1mo']);
    }

    _applyAdjCents(priceCents, adj) {
      if (!adj) return priceCents;
      const v = adj.value || 0;
      if (adj.type === 'percentage') return priceCents - (priceCents * v) / 100;
      if (adj.type === 'fixed_amount') return priceCents - v;
      if (adj.type === 'price') return v;
      return priceCents;
    }

    _mpFreqIs3mo() {
      const mp = this.config.multiProduct || {};
      const axis = mp.freqAxis || this.config.supplyAxisName;
      const val = axis ? String(this.state.selections[axis] || '') : '';
      return /3\s*mo|12\s*we/i.test(val);
    }

    // A global 3-month pill can outlive a merchant's per-variant mapping or selling
    // plan. Normalize incomplete configuration to the default cadence instead of
    // showing a quarterly selection with monthly pricing and a URL that cannot
    // produce a valid cart target.
    _normaliseUnavailableMpCadence() {
      if (!this._isMP() || this.state.mode !== 'subscribe' || !this._mpFreqIs3mo()) return;
      if (this._mpTarget()) return;
      const axis = this.config.multiProduct?.freqAxis || this.config.supplyAxisName;
      if (axis && this.config.defaultSupply) {
        this.state.selections[axis] = this.config.defaultSupply;
      }
    }

    // Pair a base variant to its 3-month counterpart from a list of 3-month variants.
    // Match by identical handle, then by the "<sku>-90" convention. Never fall back
    // to a different 3-month variant: an incomplete mapping must fail closed instead
    // of silently adding a different flavor or packet count.
    _pickThreeMonthVariant(tms, pdpVariant) {
      if (!tms || !tms.length || !pdpVariant) return null;
      const bySku = pdpVariant.sku ? pdpVariant.sku + '-90' : null;
      return (
        tms.find((v) => v.handle && pdpVariant.handle && v.handle === pdpVariant.handle) ||
        (bySku ? tms.find((v) => v.sku === bySku) : null) ||
        null
      );
    }

    _mpThreeMonthVariant(pdpVariant) {
      const mp = this.config.multiProduct || {};
      return this._pickThreeMonthVariant(mp.threeMonthVariants || [], pdpVariant);
    }

    // What the current UI state actually buys — mirrors the legacy switch table.
    _mpTarget() {
      const pdp = this._findVariant();
      if (!pdp) return null;
      const mp = this.config.multiProduct || {};
      if (this.state.mode !== 'subscribe') {
        return { variantId: pdp.id, sellingPlanId: null, available: pdp.available, kind: 'one-time-purchase' };
      }
      if (this._mpFreqIs3mo()) {
        if (!mp.threeMonthSellingPlanId) return null;
        const tm = this._mpThreeMonthVariant(pdp);
        if (tm) {
          return { variantId: tm.id, sellingPlanId: mp.threeMonthSellingPlanId, available: tm.available !== false, kind: 'multi-product' };
        }
        return null;
      }
      if (!mp.pdpSellingPlanId) return null;
      return { variantId: pdp.id, sellingPlanId: mp.pdpSellingPlanId, available: pdp.available, kind: 'default-selling-plan' };
    }

    // Headline price figures (dollars) for a PDP variant under the current mode/freq.
    _mpComputePrices(pdpVariant, mode = this.state.mode) {
      const mp = this.config.multiProduct || {};
      const adjustments = this.config.sellingPlanAdjustments || {};
      const otCents = pdpVariant.price;
      const compareAtCents = pdpVariant.compareAtPrice || 0;
      const otPrice = otCents / 100;
      const compareAt = compareAtCents > otCents ? compareAtCents / 100 : null;

      if (mode !== 'subscribe') {
        return { current: otPrice, compareAt, suffix: '', months: 1 };
      }
      if (this._mpFreqIs3mo() && mp.threeMonthSellingPlanId) {
        const tm = this._mpThreeMonthVariant(pdpVariant);
        if (tm) {
          const sub = this._applyAdjCents(tm.price, adjustments[String(mp.threeMonthSellingPlanId)]) / 100;
          // Ben's reviewed design shows the full quarterly charge, compared with
          // three one-month purchases, rather than a monthly-equivalent average.
          const base = compareAt || otPrice;
          const quarterlyBase = base * 3;
          return {
            current: sub,
            compareAt: quarterlyBase > sub ? quarterlyBase : null,
            suffix: ' every 3 months',
            months: 3,
          };
        }
      }
      const pdpSub = this._applyAdjCents(otCents, adjustments[String(mp.pdpSellingPlanId)]) / 100;
      const base = compareAt || otPrice;
      return { current: pdpSub, compareAt: base > pdpSub ? base : null, suffix: ' monthly', months: 1 };
    }

    _mpRenderPrice() {
      const compareEl = $(this, '[data-uq-price-compare]');
      const currentEl = $(this, '[data-uq-price-current]');
      const suffixEl = $(this, '[data-uq-price-suffix]');
      const pdp = this._findVariant();
      if (!pdp || !currentEl) return;
      let { current, compareAt, suffix } = this._mpComputePrices(pdp);
      const renewalPrice = current;
      current = this._applyAutoDiscountToPrice(current);
      if (this.autoDiscountConfig.enabled && (!compareAt || compareAt <= current)) {
        compareAt = renewalPrice;
      }
      currentEl.textContent = fmtMoney(current);
      if (compareEl) {
        if (compareAt && compareAt > current) {
          compareEl.textContent = fmtMoney(compareAt);
          compareEl.style.display = '';
        } else {
          compareEl.textContent = '';
          compareEl.style.display = 'none';
        }
      }
      // Same cadence hand-off as _renderPrice above.
      const cadenceMovedToRenewal = this._renderAutoDiscountMeta(renewalPrice, suffix);
      if (suffixEl) suffixEl.textContent = cadenceMovedToRenewal ? '' : suffix;
    }

    _mpRenderSavings() {
      const savingsEl = $(this, '[data-uq-savings]');
      if (!savingsEl) return;
      const pdp = this._findVariant();
      if (!pdp) return;
      const cfg = this.config;
      const pct = this._mpSavingsPct(pdp);
      const template = this.state.mode === 'subscribe' ? (cfg.bundleSavingsSub || '') : (cfg.bundleSavingsOnetime || '');
      const copy = template.replace(/\{pct\}/g, String(pct));
      // No default fallback — an empty (or whitespace-only) block setting never
      // renders the callout. Otherwise keep the guardrail: no 0%-savings callout
      // in subscribe mode.
      const shouldHide =
        !copy.trim() || (pct === 0 && (this.state.mode === 'subscribe' || !cfg.bundleSavingsOnetime));
      savingsEl.innerHTML = shouldHide ? '' : this._savingsMarkup(copy);
      this.setAttribute('data-savings-visible', shouldHide ? 'false' : 'true');
    }

    _mpSavingsPct(pdp = this._findVariant()) {
      if (!pdp) return 0;
      let { current, compareAt } = this._mpComputePrices(pdp);
      current = this._applyAutoDiscountToPrice(current);
      if (this.autoDiscountConfig.enabled && (!compareAt || compareAt <= current)) {
        compareAt = this._mpComputePrices(pdp).current;
      }
      if (compareAt && compareAt > current) {
        return Math.round(((compareAt - current) / compareAt) * 100);
      }
      return 0;
    }

    _mpUpdatePerPacketPrices() {
      const packetsAxis = this._findAxisByName('Packets');
      if (!packetsAxis) return;
      const sel = this._effectiveSelections();
      // Combo mode (legacy + anchor_product): same per-packet-count anchor price
      // override as the non-MP path above — see _updatePerPacketPrices.
      const anchorPackets = this.config.anchorPackets || [];

      packetsAxis.values.forEach((v) => {
        const count = parseInt(String(v.handle).replace(/[^0-9]/g, ''), 10) || 0;
        if (!count) return;
        const bundleEntry = anchorPackets.find((p) => p.count === count);
        let usePrice;
        let billingMonths = 1;
        if (bundleEntry) {
          usePrice = moneyToFloat(this.state.mode === 'subscribe' ? bundleEntry.subPrice : bundleEntry.otPrice);
        } else {
          const trial = { ...sel, [packetsAxis.name]: v.handle };
          const pdpVariant = this._matchVariant(trial);
          if (!pdpVariant) return;
          const computedPrice = this._mpComputePrices(pdpVariant);
          usePrice = computedPrice.current;
          billingMonths = Math.max(Number(computedPrice.months) || 1, 1);
        }
        // The pill mirrors the headline: with a code auto-applied it shows the
        // first-order (discounted) basis, not the renewal basis (Asana "New Buybox
        // Updates", 2026-09-04). Real variant totals use the headline's cents-floor
        // math; anchor totals take the exact percentage (_applyAutoDiscountExact).
        const perPacket = this._perPacketPrice(
          bundleEntry
            ? this._applyAutoDiscountExact(usePrice)
            : this._applyAutoDiscountToPrice(usePrice),
          count,
          billingMonths
        );
        const safeAxisName = cssAttrValue(packetsAxis.name);
        const safeValue = cssAttrValue(v.handle);
        const metaEl = this.querySelector(
          `[data-uq-pill][data-uq-axis="${safeAxisName}"][data-uq-value="${safeValue}"] [data-uq-pill-meta]`
        );
        if (metaEl) metaEl.textContent = `$${perPacket.toFixed(2)} / packet`;
      });
    }

    /* ─────── ATC ─────── */

    _generateBundleId() {
      if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
      return `bundle-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    }

    // Size-aware anchor: if a bundle item's product has a variant whose option value matches
    // the packet size the customer selected on this PDP, add THAT variant instead of the fixed
    // metaobject default. Items without a matching size (Defend/Promote — "1 bottle") fall back.
    _resolveBundleVariantId(item) {
      const variants = item.variants;
      if (!variants || !variants.length) return item.variant_id;
      const selected = Object.values(this.state.selections || {});
      if (!selected.length) return item.variant_id;
      // An anchor variant matches when EVERY one of its option values is currently selected on
      // this PDP — so "Unflavored / 20 packets" matches Flavor=Unflavored + Packets=20 packets.
      // Products that share no axis with the selection (Defend "1 bottle") never match → fixed.
      const matches = variants.filter(
        (v) => Array.isArray(v.values) && v.values.length && v.values.every((val) => selected.indexOf(val) !== -1)
      );
      if (!matches.length) return item.variant_id;
      const available = matches.find((v) => v.available !== false);
      return (available || matches[0]).variant_id;
    }

    _buildBundleItems() {
      const bundle = this.config.bundle;
      const bundleId = this._generateBundleId();
      const purchaseType = this.state.mode;
      return (bundle.items || []).map((item) => {
        const properties = {
          _bundle_id: bundleId,
          _bundle_name: bundle.name,
          _bundle_handle: bundle.handle,
          _bundle_version: bundle.version,
          _bundle_role: item.role,
          _bundle_position: String(item.position),
        };
        const lineItem = { id: this._resolveBundleVariantId(item), quantity: 1, properties };
        if (purchaseType === 'subscribe' && item.selling_plan_id) {
          lineItem.selling_plan = item.selling_plan_id;
        }
        return lineItem;
      });
    }

    // Legacy + bundle ATC: unbundle the PDP into the bundle's items, applying the legacy
    // per-frequency resolution to EACH item using its own `mp` payload (emitted by the
    // liquid). Per item: one-time → base variant, no plan; subscribe/1mo → base variant +
    // that item product's own 1-month plan; subscribe/3mo → that item product's paired
    // 3-month variant + 3-month plan. Items lacking `mp` data fall back to base variant +
    // 1-month plan, so an add-on with no 3-month metafield stays a 1-month line.
    _buildMpBundleItems() {
      const bundle = this.config.bundle;
      const bundleId = this._generateBundleId();
      const mode = this.state.mode;
      const is3mo = this._mpFreqIs3mo();
      return (bundle.items || []).map((item) => {
        const properties = {
          _bundle_id: bundleId,
          _bundle_name: bundle.name,
          _bundle_handle: bundle.handle,
          _bundle_version: bundle.version,
          _bundle_role: item.role,
          _bundle_position: String(item.position),
        };
        const baseId = this._resolveBundleVariantId(item);
        let variantId = baseId;
        let sellingPlanId = null;
        const mp = item.mp || {};
        if (mode === 'subscribe') {
          const baseVariant = (item.variants || []).find((v) => v.variant_id === baseId) || null;
          const tm =
            is3mo && mp.threeMonthSellingPlanId
              ? this._pickThreeMonthVariant(mp.threeMonthVariants || [], baseVariant)
              : null;
          if (tm) {
            variantId = tm.id;
            sellingPlanId = mp.threeMonthSellingPlanId;
          } else {
            sellingPlanId = mp.pdpSellingPlanId || null;
          }
        }
        const lineItem = { id: variantId, quantity: 1, properties };
        if (sellingPlanId) lineItem.selling_plan = sellingPlanId;
        return lineItem;
      });
    }

    async _addToCart() {
      const cta = $(this, '[data-uq-cta]');
      if (!cta || this._addToCartPending) return;
      this._addToCartPending = true;

      const statusEl = $(this, '[data-uq-status]');
      const setStatus = (msg, isError) => {
        if (!statusEl) return;
        statusEl.textContent = msg || '';
        statusEl.dataset.status = isError ? 'error' : 'info';
      };
      let cartCommitted = false;
      let includeGwp = false;
      let gwpClaimed = false;
      const claimGwp = () => {
        if (!includeGwp || gwpClaimed) return;
        gwpClaimed = true;
        try {
          document.dispatchEvent(new CustomEvent('gwp:claimed'));
        } finally {
          this.selectedGwpVariantId = null;
          this.selectedGwpCampaign = null;
        }
      };
      try {
        cta.disabled = true;
        cta.textContent = 'ADDING TO CART…';
        setStatus('', false);

        const isBundle = !!(this.config.bundle && (this.config.bundle.items || []).length);
        let items;
        let primaryVariantId = null;

        if (this._isMP() && isBundle) {
          // Legacy + bundle: unbundle into the bundle's items, each resolved per the
          // legacy frequency switch (see _buildMpBundleItems). primaryVariantId stays
          // null, matching the standard bundle path below.
          items = this._buildMpBundleItems();
        } else if (this._isMP()) {
          const t = this._mpTarget();
          if (!t || !t.available) {
            setStatus('This subscription option is unavailable. Please choose another option.', true);
            return;
          }
          const item = { id: t.variantId, quantity: 1 };
          if (this.state.mode === 'subscribe') item.selling_plan = t.sellingPlanId;
          items = [item];
          primaryVariantId = t.variantId;
        } else if (isBundle) {
          items = this._buildBundleItems();
        } else {
          const variant = this._findVariant();
          if (!variant || !variant.available) return;
          const item = { id: variant.id, quantity: 1 };
          if (this.state.mode === 'subscribe') {
            const sellingPlanId = this._resolveSellingPlanId(variant);
            if (!sellingPlanId) {
              setStatus('This subscription option is unavailable. Please choose another option.', true);
              return;
            }
            item.selling_plan = sellingPlanId;
          }
          items = [item];
          primaryVariantId = variant.id;
        }

        if (
          this.state.mode === 'subscribe' &&
          (!items?.length || items.some((item) => !item.selling_plan))
        ) {
          setStatus('This subscription option is unavailable. Please choose another option.', true);
          return;
        }

        if (window.UqoraCampaignOffers?.getActiveOffer?.()) {
          const campaignReady = await window.UqoraCampaignOffers.ensureCartAttribute();
          if (!campaignReady) {
            setStatus('We could not prepare this offer. Please try again.', true);
            return;
          }
        }

        const giftResult = window.UqoraCampaignOffers?.appendGiftLine?.({
          items,
          productHandle: this.config.productHandle,
          selectedVariantId: this.selectedGwpVariantId,
          selectedCampaign: this.selectedGwpCampaign,
          createBundleId: () => `gwp_${this._generateBundleId()}`,
        });
        includeGwp = Boolean(giftResult?.included);

        // Keep cart discount state in sync before adding the selected line(s),
        // matching the established single- and multi-product VWO buy boxes.
        if (window.UqoraAutoDiscount?.pendingCartUpdate) {
          try {
            await window.UqoraAutoDiscount.pendingCartUpdate;
          } catch (_) {
            // The add request still gets a chance to succeed if discount sync failed.
          }
        }
        const body = { items };
        if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
          body.sections = this.cart.getSectionsToRender().map((s) => s.id);
          body.sections_url = window.location.pathname;
        }

        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!response.ok) {
          const text = await response.text();
          throw new Error(`cart/add.js failed: ${response.status} ${text}`);
        }
        cartCommitted = true;
        let result = await response.json();

        claimGwp();

        // A code attached to an empty cart is not proof that it applies to this
        // line. Verify Shopify's post-add cart response before preserving any
        // discounted headline or auto-applied claim.
        let discountWarning = false;
        if (
          this.autoDiscountConfig.enabled &&
          window.UqoraAutoDiscount?.ensureDiscountCodeApplied
        ) {
          const verification = await window.UqoraAutoDiscount.ensureDiscountCodeApplied(
            this.autoDiscountConfig.code,
            {
              sections: body.sections || [],
              sectionsUrl: body.sections_url || window.location.pathname,
            }
          );
          if (verification.cart) result = verification.cart;
          discountWarning = !verification.ok;
        }

        let uiSyncFailed = false;
        try {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'uq-buybox',
              productVariantId: primaryVariantId,
              cartData: result,
            });
          }
        } catch (error) {
          uiSyncFailed = true;
          console.error('[uq-buybox] cart publish failed', error);
        }

        try {
          this.cart?.renderContents?.(result);
        } catch (error) {
          uiSyncFailed = true;
          console.error('[uq-buybox] cart render failed', error);
        }

        if (discountWarning) {
          setStatus(
            'Added to cart, but the promotional discount could not be applied. Review your cart.',
            true
          );
        } else if (uiSyncFailed) {
          setStatus('Added to cart. Refresh to view the updated cart.', true);
        } else {
          setStatus('Added to cart.', false);
        }
      } catch (err) {
        if (cartCommitted) {
          claimGwp();
          console.error('[uq-buybox] post-add sync failed', err);
          setStatus('Added to cart. Refresh to view the updated cart.', true);
        } else {
          console.error('[uq-buybox] ATC failed', err);
          setStatus('Could not add to cart. Please try again.', true);
        }
      } finally {
        this._addToCartPending = false;
        this._renderCta(this._findVariant());
      }
    }

    _resolveSellingPlanId(variant) {
      const plans = this.config.sellingPlans || {};
      // If the supply axis is selected, map its value to a plan id.
      const supplyName = this.config.supplyAxisName;
      if (supplyName && this.state.selections[supplyName]) {
        const supplyVal = this.state.selections[supplyName];
        const key = supplyVal.match(/3\s*mo/i)
          ? '3mo'
          : supplyVal.match(/12\s*we/i)
            ? '3mo'
            : '1mo';
        return plans[key] || null;
      }
      return plans['1mo'] || plans['default'] || null;
    }
  }

  customElements.define('uq-buybox', UQBuyBox);
})();
